import { motion } from "framer-motion";
import CountryFlag from "../components/CountryFlag";
import { useTranslation } from "react-i18next";
import {
  containerVariants,
  titleVariants,
  textVariants,
} from "../utils/animations";
import dkBg from "../assets/DK_bg.webp";

// Country code mapping
const countryCodes = {
  Dubai: "AE",
  Germany: "DE",
  India: "IN",
  USA: "US",
  "Hong Kong": "HK",
  Philippines: "PH",
  Pakistan: "PK",
};

// Mobile-optimized Venture Card Component
const VentureCard = ({ venture, index, t }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
        delay: index * 0.1,
      },
    },
  };

  const logoVariants = {
    hidden: { opacity: 0, y: 20, rotate: -10 },
    visible: {
      opacity: 1,
      y: 0,
      rotate: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
        delay: 0.2 + index * 0.1,
      },
    },
  };

  const contentVariants = {
    hidden: { opacity: 0, y: 15 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut",
        delay: 0.3 + index * 0.1,
      },
    },
  };

  return (
    <motion.div
      className="group relative bg-gradient-to-br from-gray-50 via-white to-gray-100 p-8 rounded-2xl shadow-lg hover:shadow-xl cursor-pointer border border-gray-200 overflow-hidden"
      style={{ willChange: "transform, opacity" }}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      whileHover={{ y: -4, scale: 1.01 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
    >
      {/* Flag background */}
      <div className="absolute inset-0 w-full h-full">
        <CountryFlag
          countryCode={countryCodes[venture.country]}
          alt=""
          style={{
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      </div>

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-black/40" />

      {/* Flag and sector badge */}
      <motion.div
        className="mb-6 relative z-10"
        variants={logoVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      ></motion.div>

      {/* Content */}
      <motion.div
        className="relative z-10"
        variants={contentVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-50px" }}
      >
        <h3 className="text-lg font-semibold text-white group-hover:text-gray-100 mb-3 transition-all duration-200 ease-out">
          {venture.sector}
        </h3>
        <div className="flex items-center space-x-2">
          <span className="text-gray-200 text-sm font-medium">
            {venture.country}
          </span>
          <div className="w-1 h-1 bg-gray-400 rounded-full"></div>
          <span className="text-xs text-gray-300 font-medium">
            {t("ventures.status")}
          </span>
        </div>
      </motion.div>

      {/* Subtle bottom border on hover */}
      <div className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-to-r from-gray-300 via-gray-400 to-gray-300 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-out" />
    </motion.div>
  );
};

// Ventures Section
const VenturesSection = () => {
  const { t } = useTranslation();

  const ventures = [
    { sector: t("ventures.sectors.ai"), country: "Dubai" },
    { sector: t("ventures.sectors.marketing"), country: "Germany" },
    { sector: t("ventures.sectors.marketing"), country: "India" },
    { sector: t("ventures.sectors.itConsulting"), country: "Germany" },
    { sector: t("ventures.sectors.it"), country: "India" },
    { sector: t("ventures.sectors.marketingIt"), country: "USA" },
    { sector: t("ventures.sectors.marketing"), country: "Dubai" },
    { sector: t("ventures.sectors.businessConsulting"), country: "Germany" },
    { sector: t("ventures.sectors.realEstate"), country: "Dubai" },
    { sector: t("ventures.sectors.ngo"), country: "Germany" },
    { sector: t("ventures.sectors.ngo"), country: "Philippines" },
    { sector: t("ventures.sectors.ngo"), country: "Pakistan" },
    { sector: t("ventures.sectors.ngo"), country: "India" },
    { sector: t("ventures.sectors.ngo"), country: "Dubai" },
    { sector: t("ventures.sectors.businessConsulting"), country: "Dubai" },
    { sector: t("ventures.sectors.financialServices"), country: "Hong Kong" },
    { sector: t("ventures.sectors.financialServices"), country: "Dubai" },
  ];

  return (
    <section className="pt-32 pb-20 relative overflow-hidden">
      {/* Fixed background for all devices */}
      <div
        className="fixed inset-0 w-full h-full"
        style={{
          backgroundImage: `url(${dkBg})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          zIndex: -2,
        }}
      />

      {/* Overlay for better text readability */}
      <div className="fixed inset-0 bg-black/50 z-[-1]"></div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          className="text-center mb-16"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            className="font-playfair text-4xl lg:text-5xl font-bold text-[#C5A66A] mb-6"
            variants={titleVariants}
          >
            {t("ventures.title")}
          </motion.h2>
          <motion.p
            className="text-xl text-gray-200 max-w-3xl mx-auto"
            variants={textVariants}
          >
            {t("ventures.subtitle")}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {ventures.map((venture, index) => (
            <VentureCard key={index} venture={venture} index={index} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default VenturesSection;
